import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '../helpers/LoadingSpinner';
import Routes from '../../utils/routes.js.erb';
import { authHeaders } from '../../actions';
import axios from 'axios';
import PropTypes from 'prop-types';
import Tooltip from 'rc-tooltip';
import Modal from 'react-responsive-modal';
import { closeIconPath } from '../../containers/modals/ModalRoot';
import { FollowUpDateQuestion } from './questions/FollowUpDateQuestion'
import { FollowUpFillInQuestion } from './questions/FollowUpFillInQuestion'
import { FollowUpLineQuestion } from './questions/FollowUpLineQuestion'
import { FollowUpListQuestion } from './questions/FollowUpListQuestion'
import { FollowUpTextAreaQuestion } from './questions/FollowUpTextAreaQuestion'

function byField(fieldName) {
  return (a, b) => a[fieldName] > b[fieldName] ? 1 : -1;
}

function isBlank(value) {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

function hasAnyAnswer(questions) {
  for (const question of questions) {
    if (question.answer) {
      if (question.answer.checked === true) return true;
      if (!isBlank(question.answer.value)) return true;
    }
    if (question.children && question.children.length > 0) {
      if (hasAnyAnswer(question.children)) return true;
    }
  }
  return false;
}

export const FollowUpSurvey = ({ questionnaireId, onSave, auth, statusManagementEnabled, teacherOnlyNoContactEnabled, isPurpleStatus }) => {
  const [survey, setSurvey] = useState(null);
  const [followUpComplete, setFollowUpComplete] = useState(false);
  const [teacherOnlyNoContact, setTeacherOnlyNoContact] = useState(false);
  const [_answerVersion, setAnswerVersion] = useState(0); // eslint-disable-line no-unused-vars

  const [isCreating, setIsCreating] = useState(true);
  const [createError, setCreateError] = useState(null);

  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saveHistory, setSaveHistory] = useState([]);

  const isReadOnly = !auth.isManageAsqFollowUp;

  const onAnswerChange = () => {
    setAnswerVersion(v => v + 1);
  };

  const updateSurvey = async () => {
    try {
      setIsUpdating(true);
      setUpdateError(null);

      const payload = {
        ...survey,
        follow_up_complete: followUpComplete,
        teacher_only_no_contact: teacherOnlyNoContact,
      };
      let response = await axios.put(Routes.api_dashboard_follow_up_survey_path(survey.id), payload, { headers: authHeaders() });
      const updatedSurvey = response.data;
      updatedSurvey.questions.sort(byField('position'));
      setSurvey(updatedSurvey);
      setFollowUpComplete(updatedSurvey.follow_up_complete);
      setTeacherOnlyNoContact(updatedSurvey.teacher_only_no_contact);
      setIsUpdating(false);
      onSave(); // Call onSave only on success
    } catch (e) {
      const errorMessage = e.response?.data?.error || e.message || 'Unknown error occurred';
      setUpdateError(errorMessage);
      setIsUpdating(false);
    }
  };

  const handleHistoryClick = async (e) => {
    e.preventDefault();
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const response = await fetch(
        Routes.save_history_api_dashboard_follow_up_survey_path(survey.id),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
          },
        },
      );
      const data = await response.json();
      setSaveHistory(data.partner_actions || []);
    } catch (error) {
      console.error('Error fetching save history:', error);
      setSaveHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    setIsCreating(true);
    setCreateError(null);

    axios.post(
      Routes.api_dashboard_follow_up_surveys_path(),
      { player_questionnaire_id: questionnaireId },
      { headers: authHeaders() }
    )
      .then(response => {
        const survey = response.data;
        survey.questions.sort(byField('position'));
        setSurvey(survey);
        setFollowUpComplete(survey.follow_up_complete || false);
        setTeacherOnlyNoContact(survey.teacher_only_no_contact || false);
      })
      .catch(e => {
        console.error('Create survey error:', e);
        const errorMessage = e.response?.data?.error || e.message || 'Failed to create survey';
        setCreateError(errorMessage);
      })
      .finally(() => setIsCreating(false));
  }, [questionnaireId]);

  if (isCreating) return (
    <div className='o-question-modal o-question-modal-content-centered'>
      <LoadingSpinner />
    </div>
  );

  if (createError) return (
    <div className='o-question-modal o-question-modal-content-centered'>
      <span>{createError}</span>
    </div>
  );

  if (!survey) return null;
  const questions = survey.questions;

  return (
    <div className='o-question-modal'>
      {updateError && <div className='bar error'>Something went wrong, please try again later!</div>}

      <div className='o-question-modal__form'>
        <h3>
          {survey.name}
        </h3>

        {isReadOnly ? (
          <p className='u-margin-bottom--base'>You do not currently have permission to edit this form. The form shows recommendations shared with the family following screening.</p>
        ) : (
          <p className='u-margin-bottom--base'>Please share your recommendations based on your conversation with the family following screening. Please check all boxes that apply. You will be able to edit your responses if updates are needed.</p>
        )}

        {questions.map((question, index) => {
          if (question.question_type === 'asq_followup_date') return (<FollowUpDateQuestion key={index} question={question} disabled={isUpdating || isReadOnly} onAnswerChange={onAnswerChange}></FollowUpDateQuestion>);
          if (question.question_type === 'asq_followup_fill_in') return (<FollowUpFillInQuestion key={index} question={question} disabled={isUpdating || isReadOnly} onAnswerChange={onAnswerChange}></FollowUpFillInQuestion>);
          if (question.question_type === 'asq_followup_line') return (<FollowUpLineQuestion key={index} question={question} disabled={isUpdating || isReadOnly} onAnswerChange={onAnswerChange}></FollowUpLineQuestion>);
          if (question.question_type === 'asq_followup_list') return (<FollowUpListQuestion key={index} question={question} disabled={isUpdating || isReadOnly} onAnswerChange={onAnswerChange}></FollowUpListQuestion>);
          if (question.question_type === 'asq_followup_text_area') return (<FollowUpTextAreaQuestion key={index} question={question} disabled={isUpdating || isReadOnly} onAnswerChange={onAnswerChange}></FollowUpTextAreaQuestion>);

          return null;
        })}
      </div>
      {statusManagementEnabled && (
        <div className="o-follow-up-question o-follow-up-question--highlighted u-margin-top--base">
          <div className="o-follow-up-question__checkbox">
            <input
              id="follow-up-complete-checkbox"
              className="o-follow-up-question__checkbox"
              type="checkbox"
              disabled={isUpdating || isReadOnly}
              checked={followUpComplete}
              onChange={() => {
                const newValue = !followUpComplete;
                setFollowUpComplete(newValue);
                if (newValue) setTeacherOnlyNoContact(false);
              }}
            />
          </div>
          <div className="o-follow-up-question__label">
            <label htmlFor="follow-up-complete-checkbox">
              <strong>Follow-up Form is Complete?</strong>
            </label>
          </div>
        </div>
      )}
      {statusManagementEnabled && teacherOnlyNoContactEnabled && (
        <div className="o-follow-up-question o-follow-up-question--highlighted u-margin-top--small">
          <div className="o-follow-up-question__checkbox">
            <input
              id="teacher-only-no-contact-checkbox"
              className="o-follow-up-question__checkbox"
              type="checkbox"
              disabled={isUpdating || isReadOnly || followUpComplete || !isPurpleStatus}
              checked={teacherOnlyNoContact}
              onChange={() => setTeacherOnlyNoContact(!teacherOnlyNoContact)}
            />
          </div>
          <div className="o-follow-up-question__label">
            <label htmlFor="teacher-only-no-contact-checkbox">
              <strong>Teacher only - no contact needed</strong>
            </label>
          </div>
        </div>
      )}
      {!isReadOnly && (() => {
        const isSaveDisabled = statusManagementEnabled && !hasAnyAnswer(questions) && followUpComplete;
        const saveButton = (
          <button
            onClick={updateSurvey}
            className="o-sp-button o-sp-button--base o-sp-button--primary o-sp-button--block"
            disabled={isUpdating || isSaveDisabled}
            style={{ width: '100%' }}
          >
            {isUpdating ? 'Saving...' : 'Save'}
          </button>
        );

        return (
          <div className="u-margin-top--base">
            {isSaveDisabled ? (
              <Tooltip
                placement="top"
                overlay={<span>Please complete the form before submitting</span>}
              >
                <span style={{ display: 'block', width: '100%' }}>{saveButton}</span>
              </Tooltip>
            ) : saveButton}
            <div className="c-locale-legend" style={{ textAlign: 'left' }}>
              <a href="#" onClick={handleHistoryClick}>Click to see all save history</a>
            </div>
          </div>
        );
      })()}

      <Modal
        classNames={{ overlay: 'o-modal__wrap', modal: 'o-modal o-modal__min-height row column u-margin-top--large' }}
        closeIconSvgPath={closeIconPath()}
        closeOnOverlayClick={true}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        center
      >
        <div className="u-padding--base">
          <h3 className="u-margin-bottom--base">Follow-Up Report Save History</h3>
          <div style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 150px)' }}>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <i className="o-spinner-dual"></i>
              </div>
            ) : saveHistory.length > 0 ? (
              <table className="o-table o-table--bordered" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Saved By</th>
                    <th>Saved At</th>
                  </tr>
                </thead>
                <tbody>
                  {saveHistory.map((action, index) => (
                    <tr key={index}>
                      <td>{action.saved_by}</td>
                      <td>{action.saved_at ? new Date(action.saved_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <p style={{ color: '#999', fontSize: '16px' }}>No save history yet</p>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

FollowUpSurvey.propTypes = {
  questionnaireId: PropTypes.number.isRequired,
  onSave: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  statusManagementEnabled: PropTypes.bool,
  teacherOnlyNoContactEnabled: PropTypes.bool,
  isPurpleStatus: PropTypes.bool,
}

FollowUpSurvey.defaultProps = {
  statusManagementEnabled: false,
  teacherOnlyNoContactEnabled: false,
  isPurpleStatus: false,
}
